import * as IF from "infrontjs";
import { InAppState } from "../base/InAppState";
import template from './TemplateCreateTemplate.html';

export class TemplateCreateState extends InAppState
{
    static ROUTE = '/template/create';
    static ID = 'template-create';

    async enter()
    {
        this.render();

        const dropzone = this.app.view.pageContent.querySelector('#dropzone');
        const fileUpload = this.app.view.pageContent.querySelector('#file-upload');
        const fileInfoContainer = this.app.view.pageContent.querySelector('#file-info-container');
        const form = this.app.view.pageContent.querySelector('#template-form');
        const dropzoneContainer = this.app.view.pageContent.querySelector('#dropzone-container');
        let selectedFile = null;

        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.addEventListener(dropzoneContainer, eventName, preventDefaults, false);
            this.addEventListener(document.body, eventName, preventDefaults, false);
        });

        function preventDefaults(e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Highlight drop zone when item is dragged over it
        ['dragenter', 'dragover'].forEach(eventName => {
            this.addEventListener( dropzoneContainer, eventName, () => dropzone.classList.add('drag-active'), false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            this.addEventListener( dropzoneContainer, eventName, () => dropzone.classList.remove('drag-active'), false);
        });

        // Handle dropped files
        this.addEventListener(dropzoneContainer, 'drop', handleDrop, false);

        function handleDrop(e) {
            const dt = e.dataTransfer;
            const files = dt.files;
            handleFiles(files);
        }

        // Handle file selection via click
        this.addEventListener( fileUpload,'change', function(e) {
            handleFiles(this.files);
        });

        function handleFiles(files) {
            selectedFile = null;
            if (files.length > 1) {
                displayFileInfo(null, 'Please upload only one file.');
                fileUpload.value = '';
                return;
            }

            const file = files[0];

            if (file && file.name.endsWith('.docx')) {
                selectedFile = file;
                displayFileInfo(file);
                dropzoneContainer.classList.add('hidden');
            } else {
                displayFileInfo(null, 'Invalid file type. Please upload a .docx file.');
                fileUpload.value = '';
            }
        }

        function displayFileInfo(file, errorMessage) {
            fileInfoContainer.innerHTML = '';

            if (errorMessage) {
                fileInfoContainer.innerHTML = `
                    <div class="error-message">
                        <span>${errorMessage}</span>
                    </div>
                `;
                fileInfoContainer.classList.add('visible');
                dropzoneContainer.classList.remove('hidden');
                dropzoneContainer.classList.add('error');

            } else if (file) {
                dropzoneContainer.classList.remove('error');
                const fileSize = (file.size / 1024 / 1024).toFixed(2);
                fileInfoContainer.innerHTML = `
                    <div class="file-info-box">
                        <div class="file-details">
                            <svg class="file-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            <div class="file-text">
                                <p class="file-name">${file.name}</p>
                                <p class="file-size">${fileSize} MB</p>
                            </div>
                        </div>
                        <button type="button" id="remove-file-btn" class="link text-danger remove-file-btn">
                            <i class="ti ti-trash fs-4 remove-note"></i>
                        </button>
                    </div>
                `;
                fileInfoContainer.classList.add('visible');
            } else {
                fileInfoContainer.classList.remove('visible');
            }
        }

        function removeFile() {
            selectedFile = null;
            fileUpload.value = '';
            fileInfoContainer.classList.remove('visible');
            dropzoneContainer.classList.remove('hidden');
        }

        this.addEventListener( fileInfoContainer, 'click', (e) => {
            if (e.target.closest('#remove-file-btn')) {
                removeFile();
            }
        });

        this.addEventListener( this.app.view.pageContent.querySelector( '[data-id="submit-form"]'), 'click', async (e) => {
            e.preventDefault();

            if ( !form.checkValidity() || !selectedFile )
            {
                form.classList.add('was-validated');
                if (!selectedFile)
                {
                    displayFileInfo(null, 'Please select a document to upload');
                }
            }
            else
            {
                this.app.view.blockUI( this.app.view.pageContent, "Uploading template....")
                form.classList.remove('was-validated');
                const td = IF.Helper.serializeForm( form );
                try
                {
                    const { result } = await this.app.api.post( '/template', {
                        "name" : td.name,
                        "description" : IF.Helper.trim( td.description ).length ? td.description : null,
                    });

                    if ( result && result.data?.id )
                    {
                        const fileFormData = new FormData();
                        fileFormData.append( 'file', selectedFile );

                        const uploadResponse = await fetch( `${this.app.api.getUrl()}/template/file/${result.data.id}`, {
                            method: 'POST',
                            headers: {
                                'x-sessiontoken': this.app.api._api.headers.get('x-sessiontoken'),
                                'x-version': this.app.api._api.headers.get('x-version')
                            },
                            body: fileFormData
                        });

                        if ( !uploadResponse.ok )
                        {
                            throw new Error( `File upload failed: ${uploadResponse.statusText}` );
                        }

                        this.app.view.unblockUI( this.app.view.pageContent );
                        this.app.view.toast( 'Template created successfully', 'success' );
                        this.app.router.redirect( '/template/view/' + result.data.id );
                    }
                    else
                    {
                        this.app.view.unblockUI( this.app.view.pageContent );
                        this.app.view.toast( 'Failed to create template', 'error' );
                    }
                }
                catch( e )
                {
                    console.log( e );
                    this.app.view.unblockUI( this.app.view.pageContent );
                    this.app.view.toast( 'An error occurred while creating the template', 'error' );
                }
            }
        });
    }

    render()
    {
        this.app.view.render(
            this.app.view.pageContent,
            template
        );
    }

    getContentHeaderData()
    {
        return {
            title: 'Create a new template',
            breadcrumb: [ 'Work', 'Templates', 'New template' ]
        };
    }
}
